import { I18n } from '@calendly/i18n'
import { colorStrokeStandard } from '@calendly/ui/theme'
import { groupBy } from 'lodash-es'
import moment from 'moment-timezone'

import { camelize } from 'services/string_utils'

import { allValue, canceledValue, nonHostValue, Periods } from './constants'
import sampleMeetingThumbnailSvg from './sampleMeetingThumbnail.svg'

export const formatDateRelative = (dateTime, timezone) => moment.tz(dateTime, timezone).calendar({
  sameDay: `[${I18n.t('publisher.meetings.search.date_format.today')}]`,
  nextDay: `[${I18n.t('publisher.meetings.search.date_format.tomorrow')}]`,
  nextWeek: 'ddd',
  lastDay: `[${I18n.t('publisher.meetings.search.date_format.yesterday')}]`,
  lastWeek: 'MMM D',
  sameElse(now) {
    return this.isSame(now, 'year') ? 'MMM D' : 'MM/DD/YY'
  }
})

export const buildFilters = (filterParams) => {
  if (!filterParams) return null

  const filters = camelize({
    ...filterParams,
    period: filterParams.period,
    order: 'ASC',
  })

  // Add date range if both start and end dates are provided
  if (filterParams.start_date && filterParams.end_date) {
    filters.startDate = filterParams.start_date
    filters.endDate = filterParams.end_date
  }

  Object.keys(filters).forEach((key) => {
    // Only remove if the value is null, undefined, empty array, or empty string
    if (filters[key] === null || filters[key] === undefined
      || (Array.isArray(filters[key]) && filters[key].length === 0)
      || filters[key] === '') {
      delete filters[key]
    }
  })

  return filters
}

export const mapNodeToMeeting = (node, publisherTZ) => {
  const attendees = (node.attendees || []).map((attendee) => ({ ...attendee, policy: { noShow: false, update: false } }))
  const groupedAttendees = groupBy(attendees, 'kind')
  const allInvitees = groupedAttendees.INVITEE || []
  const invitees = allInvitees.filter((inv) => !inv.cancelled)
  const hosts = groupedAttendees.HOST || []
  const cohosts = groupedAttendees.COHOST || []
  const externalAttendees = groupedAttendees.EXTERNAL || []

  const updated = {
    ...camelize(node),
    startTime: moment(node.start_time || node.startTime).tz(publisherTZ).format(),
    endTime: moment(node.end_time || node.endTime).tz(publisherTZ).format(),
    source: node.kind,
    eventType: {
      ...camelize(node.event_type || {}),
      ...(node.kind !== 'CALENDLY' ? { color: colorStrokeStandard } : {}),
      kind: allInvitees.length > 1 ? 'group' : 'solo', // TODO: we need to read this kind from the node, but it doesn't exist yet
    },
    meetingIntelligence: node.meeting_intelligence ? camelize(node.meeting_intelligence) : node.meeting_intelligence,
    invitee: invitees[0] || {},
    filledCount: invitees.length,
    eventMemberships: hosts
      .concat(cohosts)
      .concat(externalAttendees)
      .filter(Boolean),
    invitees,
    attendees,
    host: hosts[0],
  }
  return updated
}

export const isFilterSet = (filterParams) => {
  if (filterParams.period !== Periods.PAST) return true
  if (filterParams.status_ids?.includes(allValue) || filterParams.status_ids?.includes(canceledValue)) return true
  if (filterParams.event_type_ids?.length > 0) return true
  if (filterParams.host_ids?.includes(allValue) || filterParams.host_ids?.includes(nonHostValue)) return true
  if (filterParams.invitee_emails?.length > 0) return true
  if (filterParams.team_ids?.length > 0) return true
  if (filterParams.user_ids?.length > 0) return true
  if (filterParams.query?.length > 0) return true

  return false
}

export const formatThumbnailTime = (seconds) => {
  if (Number.isNaN(seconds)) return ''

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  const formattedSeconds = remainingSeconds.toString().padStart(2, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')

  return hours > 0
    ? `${hours}:${formattedMinutes}:${formattedSeconds}`
    : `${minutes}:${formattedSeconds}`
}

export const sampleMeeting = (timezone) => {
  const currentYear = moment().year()
  const eventStartTime = moment(`${currentYear}-05-01T18:00:00.000Z`).tz(timezone)
  const eventDate = formatDateRelative(eventStartTime, timezone)

  return {
    uuid: 'fake-meeting',
    name: 'Calendly Notetaker Demo',
    hostName: 'Alex Smith',
    inviteeName: 'Taylor Jones',
    emails: [],
    eventDate,
    eventStartTime,
    eventEndTime: moment(`${currentYear}-05-01T18:30:00.000Z`).tz(timezone),
    timeNotation: '12h',
    trackedMeetingUuid: undefined,
    thumbnail: sampleMeetingThumbnailSvg,
    durationSeconds: 1308,
    isFetching: false
  }
}

export const getFilenamePrefix = (trackedMeeting) => {
  if (!trackedMeeting) {
    return null
  }

  const startTime = trackedMeeting.startTime
  if (!startTime) {
    return null
  }

  // Validate the date is valid
  const eventDate = new Date(startTime)
  if (Number.isNaN(eventDate.getTime())) {
    return null
  }

  const formattedDate = moment(eventDate).format('YYYY-MM-DD')
  if (!formattedDate || formattedDate === 'Invalid date') {
    return null
  }

  // Format time as HHmm (e.g., 1030 for 10:30 AM) for filesystem-safe filename
  const formattedTime = moment(eventDate).format('HHmm')
  const name = trackedMeeting.name || 'meeting'
  // Clean name for filename (remove invalid characters)
  const cleanName = name.replace(/[^a-zA-Z0-9-_\s]/g, '').replace(/\s+/g, '_')

  return `${formattedDate}_${formattedTime}_${cleanName}`
}

export const formatTranscriptContent = (transcript) => {
  if (!transcript || !Array.isArray(transcript)) return ''
  const contentToString = (item) => `(${item.startTime}) ${item.speaker}: ${item.words}`
  const formattedContent = transcript.map((item, index) => {
    if (index > 0 && transcript[index - 1].speaker !== item.speaker) {
      return `\n${contentToString(item)}`
    }
    return contentToString(item)
  })
  return formattedContent.join('\n')
}
